/**
 * The single active phase for an AGGREGATE of upload batches (the upload-status
 * panel banner). Per-batch state comes from `deriveBatchState`; this collapses
 * the set into the one phase that should drive the banner copy.
 *
 * Priority is fixed and deliberate: a transfer in progress (`uploading`) always
 * wins over stalled / wrap-up / prepare / queued, so a batch with ANY bytes
 * moving is never mislabelled "Queued for upload".
 *
 * `stalled` ranks directly below `uploading` and ABOVE `preparing`/`queued`.
 * If anything is genuinely moving we say so; but if nothing is, a batch that
 * moved bytes and then stopped is the most informative thing we can report,
 * and it must not be outranked by another batch that merely hasn't started —
 * "Preparing…" while a live transfer is dying is precisely the wrong message.
 *
 * Callers map the returned phase to their own copy via an EXHAUSTIVE `switch`
 * (with a `never` default). That's the whole point: a phase can never again
 * silently fall through to the wrong default — which is exactly the bug this
 * exists to prevent.
 */
export type ActiveUploadPhase =
  | 'uploading'
  | 'stalled'
  | 'wrapping-up'
  | 'preparing'
  | 'queued'
  | 'processing'
  | 'idle';

export interface ActiveUploadFlags {
  uploading: boolean;
  stalled: boolean;
  wrappingUp: boolean;
  preparing: boolean;
  queued: boolean;
  processing: boolean;
}

export function deriveActiveUploadPhase(flags: ActiveUploadFlags): ActiveUploadPhase {
  if (flags.uploading) return 'uploading';
  if (flags.stalled) return 'stalled';
  if (flags.wrappingUp) return 'wrapping-up';
  if (flags.preparing) return 'preparing';
  if (flags.queued) return 'queued';
  if (flags.processing) return 'processing';
  return 'idle';
}
